import { useAuth } from "@clerk/clerk-react";
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import { AdminCsvUploadRoute } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import styles from "./styles";

const AdminCsvUpload = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [loading, setLoading] = useState(false);

  const [uploadRoute, setUploadRoute] = useState<AdminCsvUploadRoute>(
    AdminCsvUploadRoute.CREATE_CONTACTS,
  );
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [file, setFile] = useState<File | null>(null);

  const handleRouteChange = (event: CustomEvent) => {
    setUploadRoute(event.target.value as AdminCsvUploadRoute);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setFile(selectedFile || null);
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDryRun(event.target.checked);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (!file) {
        throw new Error("You must select a CSV file to upload");
      }

      const data = new FormData();
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      if (fileExt !== "csv") {
        throw new Error("You must select a CSV file");
      }
      data.append("file", file);

      data.set("dry_run", dryRun.toString());
      const url = `/api/admin/${uploadRoute}`;

      await fetcherAuth(getToken, url, "POST", {}, data, false, true);
      setAlert(`Uploaded ${uploadRoute} CSV`, "success");
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Upload CSV</strong>
          </Typography>
          <form onSubmit={handleSubmit}>
            <FormControl sx={styles.select}>
              <Select
                variant="standard"
                value={uploadRoute}
                onChange={handleRouteChange}
              >
                <MenuItem value={0} disabled>
                  Select an upload type
                </MenuItem>
                {Object.values(AdminCsvUploadRoute).map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={styles.file}>
              <Input type="file" onChange={handleFileChange} />
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={dryRun}
                  onChange={handleToggle}
                  color="primary"
                />
              }
              label={`Dry Run`}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={styles.submit}
              disabled={loading}
            >
              Submit
            </Button>
          </form>
          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Grid>
      }
    />
  );
};

export default AdminCsvUpload;
